"use client";

import React, { useEffect, useRef, useState } from "react";
type SectionLanguage = "en" | "it";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
type PartyPerson = {
  role: {
    en: string;
    it: string;
  };
  name: string;
};
type ParentPerson = {
  relation: {
    en: string;
    it: string;
  };
  names: string;
};
const groomsSide: PartyPerson[] = [{
  role: {
    en: "Best Man",
    it: "Best Man"
  },
  name: "Matthew Mann"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Lorenzo Basili"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Brian Crowe"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Cameron Emory"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Scott Plaisance"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Carlos Rodriguez-Fierro"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "Matthew Shields"
}, {
  role: {
    en: "Groomsman",
    it: "Groomsman"
  },
  name: "James Theofilos"
}];
const bridesSide: PartyPerson[] = [{
  role: {
    en: "Maid of Honor",
    it: "Maid of Honor"
  },
  name: "Vanessa Benedetti"
}, {
  role: {
    en: "Matron of Honor",
    it: "Matron of Honor"
  },
  name: "Melissa Chandler"
}, {
  role: {
    en: "Bridesmaid",
    it: "Bridesmaid"
  },
  name: "Beatrice Brizi"
}, {
  role: {
    en: "Bridesmaid",
    it: "Bridesmaid"
  },
  name: "Chiara Cirulli"
}, {
  role: {
    en: "Bridesmaid",
    it: "Bridesmaid"
  },
  name: "Megan Davenport"
}, {
  role: {
    en: "Bridesmaid",
    it: "Bridesmaid"
  },
  name: "Avan Mire"
}, {
  role: {
    en: "Bridesmaid",
    it: "Bridesmaid"
  },
  name: "Giulia Palma"
}];
const parents: ParentPerson[] = [{
  relation: {
    en: "Parents of the Groom",
    it: "Genitori dello Sposo"
  },
  names: "James and Jan Mann"
}, {
  relation: {
    en: "Parents of the Bride",
    it: "Genitori della Sposa"
  },
  names: "Alessandro and Andrea Basili"
}];
function PartyColumn({
  label,
  people,
  isVisible,
  language,
  delayOffset = 0
}: {
  label: {
    en: string;
    it: string;
  };
  people: PartyPerson[];
  isVisible: boolean;
  language: SectionLanguage;
  delayOffset?: number;
}) {
  return <div className="flex-1 min-w-0">
      <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-muted-foreground mb-6 pb-4 border-b border-border/40">
        {label[language]}
      </p>

      <div className="divide-y divide-border/30">
        {people.map((person, i) => <div key={i} className={`py-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionDelay: `${(i + delayOffset) * 100}ms`
      }}>
            <p className="font-sans text-[9px] uppercase tracking-[0.25em] text-primary mb-1">
              {person.role[language]}
            </p>
            <p className="font-serif italic text-2xl text-foreground mb-2">
              {person.name}
            </p>
          </div>)}
      </div>
    </div>;
}
export default function WeddingParty() {
  const [language, setLanguage] = useState<SectionLanguage>("en");
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: columnsRef,
    isVisible: isColumnsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: parentsRef,
    isVisible: isParentsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  const t = {
    en: {
      date: "September 11, 2026",
      title: "Wedding Party",
      groomSide: "Groom's Side",
      brideSide: "Bride's Side",
      parents: "With Gratitude To Our Parents"
    },
    it: {
      date: "11 Settembre 2026",
      title: "Damigelle e Testimoni",
      groomSide: "Lato dello Sposo",
      brideSide: "Lato della Sposa",
      parents: "Con Gratitudine ai Nostri Genitori"
    }
  }[language];
  return <section id="weddingparty" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-sans tracking-[0.4em] uppercase text-[10px] text-muted-foreground block mb-4">
            {t.date}
          </span>

          <h2 className="font-serif italic text-5xl md:text-6xl lg:text-7xl font-light text-foreground mb-6">
            {t.title}
          </h2>

          <div className="w-12 h-px bg-primary mx-auto" />
        </div>

        <div ref={columnsRef} className="flex flex-col md:flex-row gap-0 md:gap-16">
          <PartyColumn label={{
          en: t.groomSide,
          it: t.groomSide
        }} people={groomsSide} isVisible={isColumnsVisible} language={language} />

          <div className="hidden md:block w-px bg-border/30 shrink-0" />
          <div className="block md:hidden h-px bg-border/30 my-8" />

          <PartyColumn label={{
          en: t.brideSide,
          it: t.brideSide
        }} people={bridesSide} isVisible={isColumnsVisible} language={language} delayOffset={3} />
        </div>

        <div ref={parentsRef} className={`mt-20 pt-16 border-t border-border/40 transition-all duration-1000 ${isParentsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-muted-foreground text-center mb-12">
            {t.parents}
          </p>

          <div className="flex flex-col md:flex-row gap-10 md:gap-16">
            {parents.map((parent, i) => <div key={i} className={`flex-1 text-center transition-all duration-700 ${isParentsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <p className="font-sans text-[9px] uppercase tracking-[0.25em] text-primary mb-2">
                  {parent.relation[language]}
                </p>
                <p className="font-serif italic text-2xl text-foreground mb-3">
                  {parent.names}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}